import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import {
  BellIcon,
  CheckIcon,
  CheckCircleIcon,
  XMarkIcon,
  MegaphoneIcon,
  CalendarIcon,
  AcademicCapIcon,
  TruckIcon,
  BanknotesIcon,
  BookOpenIcon,
  CogIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid'
import { useNotifications } from '@/store/NotificationContext'
import { formatDistanceToNow } from 'date-fns'
import type { NotificationType } from '@/api/communicationApi'

const notificationTypeConfig: Record<NotificationType, { icon: typeof MegaphoneIcon; color: string; bgColor: string }> = {
  announcement: { icon: MegaphoneIcon, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  event_reminder: { icon: CalendarIcon, color: 'text-purple-600', bgColor: 'bg-purple-100' },
  attendance_alert: { icon: UserGroupIcon, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  transport_update: { icon: TruckIcon, color: 'text-green-600', bgColor: 'bg-green-100' },
  academic_update: { icon: AcademicCapIcon, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  fee_reminder: { icon: BanknotesIcon, color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  library_notice: { icon: BookOpenIcon, color: 'text-teal-600', bgColor: 'bg-teal-100' },
  system: { icon: CogIcon, color: 'text-gray-600', bgColor: 'bg-gray-100' }
}

interface NotificationBellProps {
  className?: string
}

export function NotificationBell({ className }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const {
    notifications,
    counts,
    isLoading,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
    hasMore,
    currentPage
  } = useNotifications()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNotificationClick = async (notification: typeof notifications[0]) => {
    if (notification.status === 'unread') {
      await markAsRead(notification.id)
    }

    if (notification.actionUrl) {
      setIsOpen(false)
      navigate(notification.actionUrl)
    }
  }

  const handleLoadMore = () => {
    if (hasMore && !isLoading) {
      fetchNotifications(currentPage + 1)
    }
  }

  const handleMarkAllRead = async () => {
    await markAllAsRead()
  }

  const handleViewAll = () => {
    setIsOpen(false)
    navigate('/notifications')
  }

  const getTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true })
    } catch {
      return 'Just now'
    }
  }

  return (
    <div className={`relative ${className || ''}`} ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        aria-label={`Notifications ${counts.unread > 0 ? `(${counts.unread} unread)` : ''}`}
      >
        {isOpen ? (
          <BellSolidIcon className="h-6 w-6 text-primary-600" />
        ) : (
          <BellIcon className="h-6 w-6" />
        )}

        {/* Unread Badge */}
        {counts.unread > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full ring-2 ring-white">
            {counts.unread > 99 ? '99+' : counts.unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 overflow-hidden z-50"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                <p className="text-xs text-gray-500">
                  {counts.unread > 0 ? `${counts.unread} unread` : 'All caught up!'}
                </p>
              </div>
              {counts.unread > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center space-x-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  <span>Mark all read</span>
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {isLoading && notifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <BellIcon className="h-12 w-12 text-gray-300 mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">No notifications yet</p>
                  <p className="text-xs text-gray-400 mt-1">
                    You'll be notified about important updates here
                  </p>
                </div>
              ) : (
                <>
                  {notifications.map((notification) => {
                    const config = notificationTypeConfig[notification.sourceType] || notificationTypeConfig.system
                    const IconComponent = config.icon

                    return (
                      <div
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-l-4 ${
                          notification.status === 'unread'
                            ? 'border-primary-500 bg-primary-50/30'
                            : 'border-transparent'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`flex-shrink-0 p-2 rounded-lg ${config.bgColor}`}>
                            <IconComponent className={`h-4 w-4 ${config.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <p className={`text-sm font-medium truncate ${
                                notification.status === 'unread' ? 'text-gray-900' : 'text-gray-600'
                              }`}>
                                {notification.title}
                              </p>
                              {notification.status === 'unread' && (
                                <span className="ml-2 flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-gray-400">
                                {getTimeAgo(notification.createdAt)}
                              </span>
                              {notification.priority === 'urgent' && (
                                <span className="text-xs font-medium text-red-600 bg-red-100 px-1.5 py-0.5 rounded">
                                  Urgent
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}

                  {/* Load More */}
                  {hasMore && (
                    <button
                      onClick={handleLoadMore}
                      disabled={isLoading}
                      className="w-full px-4 py-2 text-sm text-primary-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      {isLoading ? 'Loading...' : 'Load more'}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
              <button
                onClick={handleViewAll}
                className="w-full text-center text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                View all notifications
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default NotificationBell
